/*
 * @Author: Json Chen
 * @Date: 2021-06-22 11:32:06
 * @LastEditTime: 2022-06-02 16:16:12
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Product/index.tsx
 */

import CommonHeader from '@/src/components/CommonHeader';
import { setProductSetting } from '@/src/request/settings/product';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { Icon } from '@/src/tools/icon';
import {
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
  EPermissionSource,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Checkbox, Form, message, Spin } from 'antd';
import React from 'react';
import { useHistory } from 'react-router';
import PriceTierTable from './Components/PriceTierTable';
import ProductAttributeAdvancedTable from './Components/ProductAttributeAdvancedTable';
import ProductAttributeDefaultTable from './Components/ProductAttributeDefaultTable';
import { breadcrumbData } from './config';
import useProductSettings from './Hooks/useProductSettings';
import styles from './style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
const permissions = () => {
  const view_product_setting = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsProduct,
        operation: EnumOperation.view_product_setting,
      }),
    ],
  });
  const edit_setting = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsProduct,
        operation: EnumOperation.edit_setting,
      }),
    ],
  });
  return {
    view_product_setting,
    edit_setting,
  };
};

const Product = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => {
    return i18nGet(i18n, str);
  };
  const { view_product_setting, edit_setting } = permissions();
  const history = useHistory();

  const {
    loading: getLoading,
    reviewAccepted,
    reviewDisplayed,
    priceTierList,
    priceTierDefaultCurrency,
    priceTierCurrencyOptions,
    defaultAttrTableData,
    productAttributes,
  } = useProductSettings(!view_product_setting);

  const [form] = Form.useForm();

  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);

  const { loading: submitLoading, run: _setProductSetting } = useRequest(
    (params) => setProductSetting(params),
    {
      manual: true,
      onSuccess(result) {
        message.success('success');
        history.push(`/settings`);
      },
    },
  );

  React.useEffect(() => {
    const defaultForm: any = {};

    defaultForm.reviewAccepted = reviewAccepted;
    defaultForm.reviewDisplayed = reviewDisplayed;

    if (priceTierList && priceTierList.length) {
      defaultForm.priceTiers = priceTierList;
    }
    if (defaultAttrTableData && defaultAttrTableData.length) {
      defaultForm.defaultAttr = defaultAttrTableData;
    }

    if (productAttributes.length) {
      defaultForm.advancedAttr = productAttributes;
    }

    if (Object.keys(defaultForm).length) {
      form.setFieldsValue(defaultForm);
    }
  }, [priceTierList, defaultAttrTableData, productAttributes]);

  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };

  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      const {
        advancedAttr = [],
        defaultAttr = [],
        priceTiers = [],
        reviewAccepted,
        reviewDisplayed,
      } = res;

      // 拿到 brands defaultMeasurementUnits storeMeasurementUnits suppliers
      const params = defaultAttr.reduce((a, b) => {
        switch (b.title) {
          case 'Product Brands':
            a.brands = b.options;
            break;

          case 'Unit of Measurement':
            a.defaultMeasurementUnits = b.defaultOptions;
            a.storeMeasurementUnits = b.options;
            break;

          case 'Supplier':
            a.suppliers = b.options;
            break;
        }
        return a;
      }, {});

      const requestBody = { ...params };

      requestBody.reviewAccepted = reviewAccepted;
      requestBody.reviewDisplayed = reviewDisplayed;

      requestBody.productAttributes = advancedAttr;
      requestBody.priceTiers = priceTiers;
      _setProductSetting(requestBody);
    });
  };

  const onGoBackClick = () => {
    history.push(`/settings`);
  };

  return (
    <div className={styles.container}>
      <Spin spinning={getLoading || submitLoading}>
        <Form
          name={'product'}
          onValuesChange={onValuesChange}
          form={form}
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 24,
          }}
          className={styles.editForm}>
          <CommonHeader
            breadcrumbData={breadcrumbData({ getI18n })}
            onSaveDisable={onSaveDisable}
            onSubmit={onFormSubmit}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
          />
          <div className={styles.commonContainer}>
            {/* Product Review Feature */}
            <div className={styles.productReviewFeature}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('storeProductSetting.detail.productReviewFeature')}
                </div>
                <p className={styles.description}>
                  {getI18n(
                    'storeProductSetting.detail.productReviewFeatureTip',
                  )}
                </p>
              </div>
              <div className={styles.content}>
                <Form.Item
                  name={'reviewAccepted'}
                  valuePropName='checked'
                  className={styles.checkbox}
                  colon={false}
                  initialValue={reviewAccepted}
                  labelAlign='left'
                  extra={
                    <div className={styles.checkboxExtra}>
                      {getI18n('storeProductSetting.detail.accepttip')}
                    </div>
                  }>
                  <Checkbox disabled={!edit_setting}>
                    <span className={styles.checkboxText}>
                      {getI18n('storeProductSetting.detail.acceptproduct')}
                    </span>
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  name={'reviewDisplayed'}
                  valuePropName='checked'
                  className={styles.checkbox}
                  colon={false}
                  labelAlign='left'
                  initialValue={reviewDisplayed}
                  extra={
                    <div className={styles.checkboxExtra}>
                      {getI18n('storeProductSetting.detail.displayTip')}
                    </div>
                  }>
                  <Checkbox disabled={!edit_setting}>
                    <span className={styles.checkboxText}>
                      {getI18n(
                        'storeProductSetting.detail.displayProductReview',
                      )}
                    </span>
                  </Checkbox>
                </Form.Item>
                <div className={styles.tipContainer}>
                  <i className={styles.tipIcon}>
                    <Icon type='iconinfo' />
                  </i>
                  <span className={styles.tipText}>
                    {getI18n('storeProductSetting.detail.gotostore')}
                    <span className={styles.tip_text_link}>
                      {getI18n('storeProductSetting.detail.reviews')}
                    </span>
                    {getI18n('storeProductSetting.detail.reviewRare')}
                  </span>
                </div>
              </div>
            </div>
            {/* Product Price Tier */}
            <div className={styles.productPriceTier}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('storeProductSetting.detail.productPriceTier')}
                </div>
                <p className={styles.description}>
                  {getI18n('storeProductSetting.detail.tierTip')}
                </p>
              </div>
              <div className={styles.content}>
                <Form.Item name={'priceTiers'} className={styles.table}>
                  <PriceTierTable
                    priceTierDefaultCurrency={priceTierDefaultCurrency}
                    priceTierCurrencyOptions={priceTierCurrencyOptions}
                    disabled={!edit_setting}
                  />
                </Form.Item>
              </div>
            </div>
            {/* Default Product Attribute */}
            <div className={styles.productAttributeDefault}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'storeProductSetting.detail.defaultProductAttribute',
                  )}
                </div>
                <p className={styles.description}>
                  {getI18n('storeProductSetting.detail.defaaultTip')}
                </p>
              </div>
              <div className={styles.content}>
                <Form.Item name={'defaultAttr'} className={styles.table}>
                  <ProductAttributeDefaultTable disabled={!edit_setting} />
                </Form.Item>
              </div>
            </div>
            {/* Advanced Product Attribute */}
            <div className={styles.productAttributeAdvanced}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'storeProductSetting.detail.advancedProductAttribute',
                  )}
                </div>
                <p className={styles.description}>
                  {getI18n('storeProductSetting.detail.advancedTip')}
                </p>
              </div>
              <div className={styles.content}>
                <Form.Item name={'advancedAttr'} className={styles.table}>
                  <ProductAttributeAdvancedTable disabled={!edit_setting} />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default Product;
